import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiChevronRight,
  FiPlus,
  FiSearch,
} from "react-icons/fi";
import {
  createExpenseRequest,
  deleteExpenseRequest,
  getExpenseRequest,
  listExpensesRequest,
  updateExpenseRequest,
} from "../../services/expensesApi";
import { listAllProjectsRequest } from "../../services/projectsApi";
import { AppModal, AppModalBody, AppModalFooter } from "../../components/common/AppModal";
import {
  DetailGrid,
  DetailItem,
  DetailSection,
  ViewDetailModal,
} from "../../components/common/ViewDetailModal";
import { listVendorsRequest } from "../../services/vendorsApi";
import ExpenseReceiptUpload from "../../components/expenses/ExpenseReceiptUpload";
import ExpenseReceiptLinks from "../../components/expenses/ExpenseReceiptLinks";
import { normalizeExpenseReceipts } from "../../utils/expenseReceipts";
import Can from "../../components/auth/Can";
import TableRowActions from "../../components/common/TableRowActions";
import { PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";

const LEDGER_TYPES = [
  {
    id: "daily",
    label: "Daily Expense Ledger",
    description: "Record day-to-day project expenses with line items.",
  },
];

function emptyItemRow() {
  return { item_name: "", quantity: "1", unit_price: "0" };
}

function extractList(payload, key) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.[key])) return payload[key];
  if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  return [];
}

function ExpensesPage() {
  const { modulePermissions } = usePermissions();
  const perms = modulePermissions("expense");
  const showActions = perms.canRead || perms.canUpdate || perms.canDelete;
  const tableColCount = showActions ? 6 : 5;

  const [expenses, setExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedLedgerType, setSelectedLedgerType] = useState("");
  const [search, setSearch] = useState("");
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [pendingReceiptFiles, setPendingReceiptFiles] = useState([]);
  const [existingReceipts, setExistingReceipts] = useState([]);
  const [removedReceiptIds, setRemovedReceiptIds] = useState([]);

  const [form, setForm] = useState({
    project_id: "",
    vendor_id: "",
    expense_date: "",
    invoice_no: "",
    notes: "",
    expense_items: [emptyItemRow()],
  });

  const selectedProject = useMemo(
    () => projects.find((project) => String(project.id) === String(selectedProjectId)),
    [projects, selectedProjectId],
  );

  const selectedLedger = useMemo(
    () => LEDGER_TYPES.find((ledger) => ledger.id === selectedLedgerType),
    [selectedLedgerType],
  );

  const isLedgerReady = Boolean(selectedProjectId && selectedLedgerType);
  const setupStep = !selectedProjectId ? "project" : !selectedLedgerType ? "ledger" : "entries";

  const normalizeExpense = (expense) => {
    const items = Array.isArray(expense?.items)
      ? expense.items
      : Array.isArray(expense?.expense_items)
        ? expense.expense_items
        : [];
    const normalizedItems = items.map((item) => ({
      id: item?.id,
      item_name: item?.item_name || "",
      quantity: String(item?.quantity ?? "1"),
      unit_price: String(item?.unit_price ?? "0"),
      amount: Number(
        item?.amount ?? Number(item?.quantity || 0) * Number(item?.unit_price || 0),
      ),
    }));

    return {
      id: expense?.id,
      project_id: Number(expense?.project_id) || "",
      vendor_id: Number(expense?.vendor_id) || "",
      expense_date: (expense?.expense_date || expense?.date || "").slice(0, 10),
      invoice_no: expense?.invoice_no || "",
      notes: expense?.notes || "",
      total_amount: Number(expense?.total_amount || 0),
      created_at: (expense?.created_at || "-").slice(0, 10),
      updated_at: (expense?.updated_at || "-").slice(0, 10),
      project_name:
        expense?.project?.name ||
        projects.find((project) => Number(project.id) === Number(expense?.project_id))?.name ||
        "-",
      vendor_name:
        expense?.vendor?.name ||
        vendors.find((vendor) => Number(vendor.id) === Number(expense?.vendor_id))?.name ||
        "-",
      expense_items: normalizedItems,
      receipts: normalizeExpenseReceipts(expense),
    };
  };

  const resetReceiptState = () => {
    setPendingReceiptFiles([]);
    setExistingReceipts([]);
    setRemovedReceiptIds([]);
  };

  const handleAddReceiptFiles = (files) => {
    setPendingReceiptFiles((prev) => [...prev, ...files]);
  };

  const handleRemovePendingReceipt = (index) => {
    setPendingReceiptFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingReceipt = (receiptId) => {
    setExistingReceipts((prev) => prev.filter((receipt) => receipt.id !== receiptId));
    const idStr = String(receiptId);
    if (!idStr.startsWith("url-") && !idStr.startsWith("legacy-") && !idStr.startsWith("receipt-")) {
      setRemovedReceiptIds((prev) => [...prev, receiptId]);
    }
  };

  const loadProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const response = await listAllProjectsRequest();
      setProjects(extractList(response.data, "projects"));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load projects.");
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await listVendorsRequest({ per_page: 200 });
      setVendors(extractList(response.data, "vendors"));
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load vendors.");
    }
  };

  const loadExpenses = async () => {
    if (!selectedProjectId) return;

    try {
      setIsLoading(true);
      const response = await listExpensesRequest({
        search: search.trim() || undefined,
        project_id: selectedProjectId,
        page,
        per_page: perPage,
      });
      const payload = response.data;
      const list = extractList(payload, "expenses").map(normalizeExpense);
      setExpenses(list);

      const fallbackTotal = list.length;
      const resolvedTotal =
        payload?.total ??
        payload?.meta?.total ??
        payload?.data?.total ??
        payload?.data?.meta?.total ??
        fallbackTotal;
      const resolvedLastPage =
        payload?.last_page ??
        payload?.meta?.last_page ??
        payload?.data?.last_page ??
        payload?.data?.meta?.last_page ??
        Math.max(1, Math.ceil(resolvedTotal / perPage));

      setTotalRecords(Number(resolvedTotal) || 0);
      setTotalPages(Number(resolvedLastPage) || 1);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to load expenses.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    loadVendors();
  }, []);

  useEffect(() => {
    if (!isLedgerReady) {
      setExpenses([]);
      setTotalRecords(0);
      setTotalPages(1);
      return;
    }
    loadExpenses();
  }, [page, search, selectedProjectId, isLedgerReady, projects.length, vendors.length]);

  useEffect(() => {
    if (page > totalPages && totalPages > 0) setPage(totalPages);
  }, [page, totalPages]);

  const totalFromItems = useMemo(
    () =>
      form.expense_items.reduce((sum, item) => {
        const qty = Number(item.quantity || 0);
        const rate = Number(item.unit_price || 0);
        return sum + qty * rate;
      }, 0),
    [form.expense_items],
  );

  const handleProjectSelect = (projectId) => {
    setSelectedProjectId(String(projectId));
    setSelectedLedgerType("");
    setPage(1);
    setSearch("");
  };

  const handleLedgerSelect = (ledgerId) => {
    setSelectedLedgerType(ledgerId);
    setPage(1);
    setSearch("");
  };

  const resetToProjectStep = () => {
    setSelectedProjectId("");
    setSelectedLedgerType("");
    setPage(1);
    setSearch("");
  };

  const resetToLedgerStep = () => {
    setSelectedLedgerType("");
    setPage(1);
    setSearch("");
  };

  const openCreateModal = () => {
    if (!isLedgerReady) {
      toast.error("Select a project and ledger type first.");
      return;
    }
    setEditingExpense(null);
    resetReceiptState();
    setForm({
      project_id: String(selectedProjectId),
      vendor_id: "",
      expense_date: new Date().toISOString().slice(0, 10),
      invoice_no: "",
      notes: "",
      expense_items: [emptyItemRow()],
    });
    setIsModalOpen(true);
  };

  const openEditModal = async (expense) => {
    setEditingExpense(expense);
    resetReceiptState();
    setIsModalOpen(true);
    try {
      const response = await getExpenseRequest(expense.id);
      const normalized = normalizeExpense(response.data?.data || response.data || expense);
      setExistingReceipts(normalized.receipts || []);
      setForm({
        project_id: String(normalized.project_id || selectedProjectId || ""),
        vendor_id: String(normalized.vendor_id || ""),
        expense_date: normalized.expense_date,
        invoice_no: normalized.invoice_no,
        notes: normalized.notes,
        expense_items:
          normalized.expense_items.length > 0
            ? normalized.expense_items.map((item) => ({
                item_name: item.item_name,
                quantity: String(item.quantity),
                unit_price: String(item.unit_price),
              }))
            : [emptyItemRow()],
      });
    } catch {
      setExistingReceipts(normalizeExpenseReceipts(expense));
      setForm({
        project_id: String(expense.project_id || selectedProjectId || ""),
        vendor_id: String(expense.vendor_id || ""),
        expense_date: expense.expense_date,
        invoice_no: expense.invoice_no || "",
        notes: expense.notes || "",
        expense_items:
          (expense.items || expense.expense_items)?.map((item) => ({
            item_name: item.item_name,
            quantity: String(item.quantity),
            unit_price: String(item.unit_price),
          })) || [emptyItemRow()],
      });
    }
  };

  const openViewModal = async (expense) => {
    try {
      const response = await getExpenseRequest(expense.id);
      setViewingExpense(normalizeExpense(response.data?.data || response.data || expense));
    } catch {
      setViewingExpense(expense);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
    resetReceiptState();
    setForm({
      project_id: "",
      vendor_id: "",
      expense_date: "",
      invoice_no: "",
      notes: "",
      expense_items: [emptyItemRow()],
    });
  };

  const updateItem = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      expense_items: prev.expense_items.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addItem = () =>
    setForm((prev) => ({ ...prev, expense_items: [...prev.expense_items, emptyItemRow()] }));

  const removeItem = (index) =>
    setForm((prev) => {
      const nextItems = prev.expense_items.filter((_, i) => i !== index);
      return {
        ...prev,
        expense_items: nextItems.length > 0 ? nextItems : [emptyItemRow()],
      };
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.project_id) {
      toast.error("Project is required.");
      return;
    }
    const items = form.expense_items
      .map((item) => ({
        item_name: item.item_name.trim(),
        quantity: Number(item.quantity || 0),
        unit_price: Number(item.unit_price || 0),
      }))
      .filter((item) => item.item_name && item.quantity > 0);
    if (items.length === 0) {
      toast.error("Add at least one valid expense item.");
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        project_id: Number(form.project_id),
        vendor_id: form.vendor_id ? Number(form.vendor_id) : null,
        expense_date: form.expense_date || null,
        invoice_no: form.invoice_no.trim() || null,
        notes: form.notes.trim() || null,
        total_amount: Number(
          items.reduce((sum, item) => sum + item.quantity * item.unit_price, 0).toFixed(2),
        ),
        items: items.map((item) => ({
          ...item,
          amount: Number((item.quantity * item.unit_price).toFixed(2)),
        })),
      };

      const fileOptions = {
        files: pendingReceiptFiles,
        removeReceiptIds: removedReceiptIds,
      };

      if (editingExpense?.id) {
        await updateExpenseRequest(editingExpense.id, payload, fileOptions);
        toast.success("Expense updated successfully.");
      } else {
        await createExpenseRequest(payload, fileOptions);
        toast.success("Expense created successfully.");
      }
      await loadExpenses();
      closeModal();
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          (editingExpense ? "Failed to update expense." : "Failed to create expense."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteExpenseId) return;
    try {
      await deleteExpenseRequest(deleteExpenseId);
      toast.success("Expense deleted.");
      setDeleteExpenseId(null);
      await loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete expense.");
    }
  };

  return (
    <main className="w-full min-w-0 bg-base-100">
      <section className="w-full border-b border-base-200 bg-base-100">
        <div className="border-b border-base-200 px-4 py-8 sm:px-8">
          <h1 className="ui-page-title tracking-tight">Expenses Ledger</h1>
          <p className="ui-muted mt-2">
            {setupStep === "project" &&
              "Step 1 — Select a project to open its expense ledgers."}
            {setupStep === "ledger" && "Step 2 — Choose a ledger type for this project."}
            {setupStep === "entries" &&
              "Manage expense entries for the selected project and ledger type."}
          </p>
        </div>

        {!isLedgerReady ? (
          <div className="px-4 py-6 sm:px-8">
            {setupStep === "ledger" ? (
              <button
                type="button"
                className="btn btn-ghost btn-sm mb-4 gap-2 rounded-lg"
                onClick={resetToProjectStep}
              >
                <FiArrowLeft className="h-4 w-4" />
                Change project
              </button>
            ) : null}

            {setupStep === "project" ? (
              <>
                <p className="ui-panel-title mb-4">Select project</p>
                {isLoadingProjects ? (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div key={`project-skeleton-${index}`} className="skeleton h-20 rounded-xl" />
                    ))}
                  </div>
                ) : projects.length === 0 ? (
                  <div className="alert rounded-xl bg-base-200/70">
                    <span className="ui-nav-text">No projects found. Create a project first.</span>
                  </div>
                ) : (
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        className="flex items-center justify-between rounded-xl border border-base-300 bg-white p-4 text-left transition hover:border-primary hover:shadow-sm"
                        onClick={() => handleProjectSelect(project.id)}
                      >
                        <div className="min-w-0">
                          <p className="ui-name-strong truncate">{project.name}</p>
                          {project.slug ? (
                            <p className="ui-meta mt-1 truncate">{project.slug}</p>
                          ) : null}
                        </div>
                        <FiChevronRight className="h-5 w-5 shrink-0 text-base-content/40" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="ui-panel-title mb-1">Ledger type</p>
                <p className="ui-muted mb-4">
                  Project: <span className="font-semibold text-base-content">{selectedProject?.name}</span>
                </p>
                <div className="grid gap-3 sm:grid-cols-2">
                  {LEDGER_TYPES.map((ledger) => (
                    <button
                      key={ledger.id}
                      type="button"
                      className="flex items-start justify-between rounded-xl border border-base-300 bg-white p-4 text-left transition hover:border-primary hover:shadow-sm"
                      onClick={() => handleLedgerSelect(ledger.id)}
                    >
                      <div>
                        <p className="ui-name-strong">{ledger.label}</p>
                        <p className="ui-muted mt-1">{ledger.description}</p>
                      </div>
                      <FiChevronRight className="mt-1 h-5 w-5 shrink-0 text-base-content/40" />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 border-b border-base-200 px-4 py-3 sm:px-8">
              <button
                type="button"
                className="btn btn-ghost btn-xs rounded-lg"
                onClick={resetToProjectStep}
              >
                {selectedProject?.name}
              </button>
              <span className="text-base-content/30">/</span>
              <button
                type="button"
                className="btn btn-ghost btn-xs rounded-lg"
                onClick={resetToLedgerStep}
              >
                {selectedLedger?.label}
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-8">
              <label className="input input-bordered relative flex min-h-11 min-w-0 flex-1 items-center gap-3 rounded-xl border-base-300 bg-white pl-4 pr-3 sm:max-w-xl">
                <FiSearch className="h-4 w-4 shrink-0 text-base-content/45" aria-hidden />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  className="grow bg-transparent outline-none placeholder:text-base-content/40 ui-nav-text"
                  placeholder="Search by invoice or note..."
                  autoComplete="off"
                />
              </label>

              <Can permission={PERMISSIONS.EXPENSE_CREATE}>
                <button type="button" className="ui-create-btn" onClick={openCreateModal}>
                  <FiPlus className="ui-create-btn-icon" />
                  Add daily entry
                </button>
              </Can>
            </div>

            <div className="overflow-x-auto border-t border-base-200 px-4 sm:px-8">
              <table className="table table-zebra table-pin-rows table-md min-w-[980px] w-full">
                <thead className="border-b border-base-300 bg-base-200/70">
                  <tr>
                    <th className="ui-table-header">DATE</th>
                    <th className="ui-table-header">INVOICE</th>
                    <th className="ui-table-header">VENDOR</th>
                    <th className="ui-table-header">NOTES</th>
                    <th className="ui-table-header text-right">TOTAL AMOUNT</th>
                    {showActions ? (
                      <th className="ui-table-header ui-table-actions-col">ACTIONS</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {isLoading
                    ? Array.from({ length: 5 }).map((_, index) => (
                        <tr key={`skeleton-${index}`}>
                          {Array.from({ length: tableColCount }).map((__, innerIndex) => (
                            <td key={`cell-${innerIndex}`}>
                              <div className="skeleton my-2 h-4 w-full rounded-md" />
                            </td>
                          ))}
                        </tr>
                      ))
                    : expenses.map((expense) => (
                        <tr key={expense.id}>
                          <td className="ui-nav-text">{expense.expense_date || "-"}</td>
                          <td className="ui-nav-text">{expense.invoice_no || "-"}</td>
                          <td className="ui-nav-text">{expense.vendor_name || "-"}</td>
                          <td className="ui-nav-text">{expense.notes || "-"}</td>
                          <td className="ui-nav-text text-right font-semibold">
                            {Number(expense.total_amount || 0).toFixed(2)}
                          </td>
                          {showActions ? (
                            <td className="ui-table-actions-col">
                              <TableRowActions
                                readPermission={PERMISSIONS.EXPENSE_READ}
                                updatePermission={PERMISSIONS.EXPENSE_UPDATE}
                                deletePermission={PERMISSIONS.EXPENSE_DELETE}
                                onView={() => openViewModal(expense)}
                                onEdit={() => openEditModal(expense)}
                                onDelete={() => setDeleteExpenseId(expense.id)}
                                viewLabel="View expense"
                                editLabel="Edit expense"
                                deleteLabel="Delete expense"
                              />
                            </td>
                          ) : null}
                        </tr>
                      ))}
                  {!isLoading && expenses.length === 0 ? (
                    <tr>
                      <td colSpan={tableColCount} className="py-14 align-middle">
                        <div className="flex w-full items-center justify-center">
                          <div className="alert inline-flex rounded-xl bg-base-200/70 px-6">
                            <span className="ui-nav-text text-center">No ledger entries found.</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-base-200 px-4 py-4 sm:px-8">
              <p className="ui-meta">
                Showing {totalRecords === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
                {Math.min(page * perPage, totalRecords)} of {totalRecords} entries
              </p>
              <div className="join">
                <button
                  type="button"
                  className="join-item btn btn-sm rounded-l-lg"
                  disabled={page === 1}
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }).map((_, index) => {
                  const current = index + 1;
                  return (
                    <button
                      key={current}
                      type="button"
                      className={`join-item btn btn-sm ${page === current ? "btn-active" : ""}`}
                      onClick={() => setPage(current)}
                    >
                      {current}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="join-item btn btn-sm rounded-r-lg"
                  disabled={page === totalPages}
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <AppModal
        open={isModalOpen}
        onClose={closeModal}
        title={editingExpense ? "Update Expense Entry" : "Create Expense Entry"}
        subtitle={`Project: ${selectedProject?.name || "-"} · ${selectedLedger?.label || "Ledger"}`}
        size="lg"
      >
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={handleSubmit}>
          <AppModalBody>
              <input type="hidden" value={form.project_id} readOnly />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text ui-label-form">Project</span>
                  </label>
                  <input
                    type="text"
                    className="input input-bordered rounded-xl bg-base-200/50"
                    value={selectedProject?.name || ""}
                    readOnly
                  />
                </div>
                <div className="form-control">
                  <label className="label">
                    <span className="label-text ui-label-form">Vendor</span>
                  </label>
                  <select
                    className="select select-bordered rounded-xl"
                    value={form.vendor_id}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, vendor_id: event.target.value }))
                    }
                  >
                    <option value="">Select vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text ui-label-form">Expense Date</span>
                  </label>
                  <input
                    type="date"
                    value={form.expense_date}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, expense_date: event.target.value }))
                    }
                    className="input input-bordered rounded-xl"
                  />
                </div>
                <div className="form-control sm:col-span-2">
                  <label className="label">
                    <span className="label-text ui-label-form">Invoice No</span>
                  </label>
                  <input
                    type="text"
                    value={form.invoice_no}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, invoice_no: event.target.value }))
                    }
                    className="input input-bordered rounded-xl"
                  />
                </div>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text ui-label-form">Notes</span>
                </label>
                <textarea
                  value={form.notes}
                  onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
                  className="textarea textarea-bordered min-h-[90px] rounded-xl"
                />
              </div>

              <ExpenseReceiptUpload
                pendingFiles={pendingReceiptFiles}
                existingReceipts={existingReceipts}
                onAddFiles={handleAddReceiptFiles}
                onRemovePending={handleRemovePendingReceipt}
                onRemoveExisting={handleRemoveExistingReceipt}
              />

              <div className="ui-modal-panel">
                <div className="mb-3 flex items-center justify-between">
                  <p className="ui-panel-title">Expense Items</p>
                  <button type="button" className="btn btn-sm rounded-lg" onClick={addItem}>
                    <FiPlus className="h-4 w-4" />
                    Add item
                  </button>
                </div>
                <div className="space-y-4">
                  {form.expense_items.map((item, index) => (
                    <div
                      key={`item-${index}`}
                      className="ui-modal-item-row"
                    >
                      <div className="form-control sm:col-span-5">
                        <label className="label">
                          <span className="label-text ui-label-form text-xs">Name</span>
                        </label>
                        <input
                          type="text"
                          className="input input-bordered w-full"
                          placeholder="e.g. cement"
                          value={item.item_name}
                          onChange={(event) => updateItem(index, "item_name", event.target.value)}
                        />
                      </div>
                      <div className="form-control sm:col-span-2">
                        <label className="label">
                          <span className="label-text ui-label-form text-xs">Quantity</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="input input-bordered w-full"
                          value={item.quantity}
                          onChange={(event) => updateItem(index, "quantity", event.target.value)}
                        />
                      </div>
                      <div className="form-control sm:col-span-3">
                        <label className="label">
                          <span className="label-text ui-label-form text-xs">Unit price</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="input input-bordered w-full"
                          value={item.unit_price}
                          onChange={(event) => updateItem(index, "unit_price", event.target.value)}
                        />
                      </div>
                      <div className="form-control sm:col-span-2">
                        <label className="label">
                          <span className="label-text ui-label-form text-xs">Amount</span>
                        </label>
                        <div className="flex min-h-8 items-center justify-between gap-2">
                          <span className="ui-nav-text font-semibold">
                            {(Number(item.quantity || 0) * Number(item.unit_price || 0)).toFixed(2)}
                          </span>
                          <button
                            type="button"
                            className="btn btn-ghost btn-xs shrink-0 text-error"
                            onClick={() => removeItem(index)}
                            disabled={form.expense_items.length === 1}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

          </AppModalBody>
          <AppModalFooter>
            <p className="mr-auto text-xs font-medium text-base-content/70">
              Total: <span className="font-semibold text-primary">{totalFromItems.toFixed(2)}</span>
            </p>
            <button type="button" className="btn btn-ghost" onClick={closeModal}>
              Cancel
            </button>
            <button
              type="submit"
              className={`btn btn-primary ${isSubmitting ? "btn-disabled" : ""}`}
              disabled={isSubmitting}
            >
              {isSubmitting
                ? editingExpense
                  ? "Updating..."
                  : "Creating..."
                : editingExpense
                  ? "Update Entry"
                  : "Create Entry"}
            </button>
          </AppModalFooter>
        </form>
      </AppModal>

      <ViewDetailModal
        open={!!viewingExpense}
        onClose={() => setViewingExpense(null)}
        title={viewingExpense?.invoice_no || "Expense entry"}
        subtitle={`${viewingExpense?.project_name || "-"} · ${viewingExpense?.expense_date || "-"}`}
        size="lg"
        meta={
          viewingExpense ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-xs text-base-content/65">
                Vendor: <span className="font-medium text-base-content">{viewingExpense.vendor_name || "-"}</span>
              </span>
              <span className="text-sm font-semibold text-primary">
                {Number(viewingExpense.total_amount || 0).toFixed(2)}
              </span>
            </div>
          ) : null
        }
      >
        {viewingExpense ? (
          <>
            <DetailGrid>
              <DetailItem label="Project" value={viewingExpense.project_name} />
              <DetailItem label="Invoice" value={viewingExpense.invoice_no || "-"} />
              <DetailItem label="Date" value={viewingExpense.expense_date || "-"} />
              <DetailItem label="Vendor" value={viewingExpense.vendor_name || "-"} />
              <DetailItem label="Notes" span={2} value={viewingExpense.notes || "-"} />
            </DetailGrid>
            <DetailSection title="Line items">
              <div className="overflow-x-auto">
                <table className="table table-xs">
                  <thead>
                    <tr>
                      <th className="ui-table-header">ITEM</th>
                      <th className="ui-table-header text-right">QTY</th>
                      <th className="ui-table-header text-right">UNIT</th>
                      <th className="ui-table-header text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewingExpense.expense_items || []).map((item, index) => (
                      <tr key={`${item.id || item.item_name}-${index}`}>
                        <td className="text-sm">{item.item_name}</td>
                        <td className="text-right text-sm">{Number(item.quantity || 0)}</td>
                        <td className="text-right text-sm">{Number(item.unit_price || 0).toFixed(2)}</td>
                        <td className="text-right text-sm font-medium">{Number(item.amount || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DetailSection>
            <DetailSection title="Receipts">
              <ExpenseReceiptLinks receipts={viewingExpense.receipts} />
            </DetailSection>
          </>
        ) : null}
      </ViewDetailModal>

      <AppModal
        open={!!deleteExpenseId}
        onClose={() => setDeleteExpenseId(null)}
        title="Delete Expense Entry"
        subtitle="Are you sure you want to delete this entry?"
        size="sm"
      >
        <AppModalBody className="py-1">
          <p className="text-xs text-base-content/65">This action cannot be undone.</p>
        </AppModalBody>
        <AppModalFooter>
          <button type="button" className="btn btn-ghost" onClick={() => setDeleteExpenseId(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-error" onClick={handleDelete}>
            Delete
          </button>
        </AppModalFooter>
      </AppModal>

    </main>
  );
}

export default ExpensesPage;

